import type { EventWithItems, JoinEventResponse } from '@trueup/shared';

const API_BASE = '/api';

class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchApi<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const sessionToken = localStorage.getItem('sessionToken');

  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) || {}),
  };

  if (sessionToken) {
    headers['X-Session-Token'] = sessionToken;
  }

  // Only set Content-Type for non-FormData requests
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new ApiError(response.status, error.error || 'Request failed');
  }

  return response.json();
}

export const api = {
  // Get event by share code
  async getEvent(code: string): Promise<{ event: EventWithItems }> {
    return fetchApi(`/events/code/${code}`);
  },

  // Join an event
  async joinEvent(code: string, name: string): Promise<JoinEventResponse> {
    const response = await fetchApi<JoinEventResponse>(`/events/code/${code}/join`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });

    // Store session token
    if (response.token) {
      localStorage.setItem('sessionToken', response.token);
      localStorage.setItem(`participant_${code}`, JSON.stringify(response.participant));
    }

    return response;
  },

  // Claim an item
  async claimItem(itemId: string, quantity: number = 1): Promise<void> {
    await fetchApi('/claims', {
      method: 'POST',
      body: JSON.stringify({ itemId, quantity }),
    });
  },

  // Unclaim an item
  async unclaimItem(itemId: string): Promise<void> {
    await fetchApi(`/claims/${itemId}`, {
      method: 'DELETE',
    });
  },

  // Update claim quantity
  async updateClaim(itemId: string, quantity: number): Promise<void> {
    await fetchApi(`/claims/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify({ quantity }),
    });
  },

  // Get stored participant for event
  getStoredParticipant(code: string) {
    const stored = localStorage.getItem(`participant_${code}`);
    return stored ? JSON.parse(stored) : null;
  },

  // Clear session
  clearSession(code: string) {
    localStorage.removeItem('sessionToken');
    localStorage.removeItem(`participant_${code}`);
  },
};

export { ApiError };
